// Story 175 (CU8-CU10, contract §2/§4) -- how the delivery of a fabrication order is WORDED and COLOURED.
// Nothing here classifies, counts or orders (INV-I): the status, the deviation and the week of every date
// arrive from Sun; this only picks the literal, the palette colour and the "dd/mm · Sxx" shape.
import { dayMonthOf, formatSignedDays, weekNumberOfPeriodKey } from './loadCapacityFormat'

// Contract §1/§2/§4 -- the four literals Sun sends, in the order the counters are painted (S118).
export const DELIVERY_STATUSES = ['LATE', 'ON_TIME', 'EARLY', 'NO_TARGET_DATE']

// RN29 -- clock LATE red · ✓ ON TIME green · ↑ EARLY blue · "No date" grey (icons live in the component).
// Story 204 (RN1.5): the label is the status code itself, translated through systemValueLabel ('DeliveryStatus').
export const DELIVERY_STATUS_PILL = {
  LATE: { color: 'error' },
  ON_TIME: { color: 'success' },
  EARLY: { color: 'info' },
  NO_TARGET_DATE: { color: 'default' }
}

// RN29 -- "+21 d" red when LATE, "−9 d" blue when EARLY, grey otherwise.
const DEVIATION_COLOR = { LATE: 'error.main', EARLY: 'info.main' }
export const deviationColor = (deliveryStatus) => DEVIATION_COLOR[deliveryStatus] ?? 'text.secondary'

export const NO_VALUE = '–'

const isPresent = (value) => value !== null && value !== undefined

// RN27 -- a row without a delivery status is a fabrication order WITHOUT classification (or a degraded
// answer, RN56): its four delivery cells stay empty, which is not the same as "No date".
export const isClassified = (row) => isPresent(row?.deliveryStatus)

// RN28 -- "29/08 · S35": the day and the ISO week exactly as Sun wrote them.
export const dayMonthWeek = (isoDate, periodKey, t_) => {
  const week = weekNumberOfPeriodKey(periodKey)
  const day = dayMonthOf(isoDate)
  return week === null ? day : `${day} · ${t_('W{{week}}', { week })}`
}

export const requiredDateText = (row, t_) => {
  if (!isClassified(row)) return ''
  return isPresent(row.requiredDate) ? dayMonthWeek(row.requiredDate, row.requiredPeriodKey, t_) : NO_VALUE
}

export const expectedEndText = (row, t_) => {
  if (!isClassified(row) || !isPresent(row.expectedEndDate)) return ''
  return dayMonthWeek(row.expectedEndDate, row.expectedEndPeriodKey, t_)
}

export const deviationText = (row, t_) => {
  if (!isClassified(row)) return ''
  return isPresent(row.deliveryDeviationDays) ? t_('{{days}} d', { days: formatSignedDays(row.deliveryDeviationDays) }) : NO_VALUE
}

// RN42 (GATE 2) -- the CSV of the "Required date" column: `requiredDate` as "dd/mm/yyyy", empty without a
// date (a string rearrangement of Sun's day, never the `deliveryDate` instant read in the browser's zone).
export const requiredDateCsv = (row) => {
  const isoDate = row?.requiredDate
  if (typeof isoDate !== 'string' || isoDate.length < 10) return ''
  return `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}/${isoDate.slice(0, 4)}`
}

// CU16 / S134 / S135 / S158 -- Sun's stable `code`s with a literal of their own (two reused, one new);
// anything else -- METRICS_UNAVAILABLE, DELIVERY_SORT_UNAVAILABLE, INVALID_TIME_ZONE, an unknown code or no
// code at all -- falls back to the delivery literal of the site. The raw code never reaches the user.
const DELIVERY_ERROR_KEY_BY_CODE = {
  PLAN_NOT_FOUND: 'Planification not found',
  HORIZON_TOO_LONG: 'Plan horizon exceeds the 104 week limit',
  INVALID_DELIVERY_STATUS: 'Unknown delivery status'
}
export const DELIVERY_GENERIC_ERROR_KEY = 'Delivery status could not be calculated'
export const deliveryErrorMessageKey = (error) => DELIVERY_ERROR_KEY_BY_CODE[error?.data?.code] ?? DELIVERY_GENERIC_ERROR_KEY
